import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';

type ReportType = 'orders' | 'logistics' | 'grn' | 'job-orders' | 'stocks';

// Typeahead component with dropdown suggestions
interface TypeaheadProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
  loading?: boolean;
}

const Typeahead: React.FC<TypeaheadProps> = ({ label, value, onChange, options, placeholder, loading }) => {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(value);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { setInputVal(value); }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o =>
    o.toLowerCase().includes(inputVal.toLowerCase())
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>{label}</label>
      <input
        type="text"
        value={inputVal}
        placeholder={placeholder || 'Type to search...'}
        onChange={(e) => { setInputVal(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', width: '100%', background: 'white' }}
      />
      {open && (filtered.length > 0 || loading) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflow: 'auto', marginTop: '2px'
        }}>
          {loading && <div style={{ padding: '8px 12px', fontSize: '12px', color: '#94a3b8' }}>Loading...</div>}
          {!loading && filtered.length === 0 && inputVal && (
            <div style={{ padding: '8px 12px', fontSize: '12px', color: '#94a3b8' }}>No matches</div>
          )}
          {filtered.map((opt, idx) => (
            <div
              key={idx}
              onMouseDown={() => { setInputVal(opt); onChange(opt); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReportsModule: React.FC = () => {
  const [activeReport, setActiveReport] = useState<ReportType | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportSuccess, setExportSuccess] = useState('');

  // Shared date filters
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Orders filters
  const [orderStatus, setOrderStatus] = useState('');
  const [orderChannel, setOrderChannel] = useState('');
  const [orderSku, setOrderSku] = useState('');
  const [shippingStatus, setShippingStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');

  // Logistics filters
  const [logisticsStatus, setLogisticsStatus] = useState('');
  const [logisticsProvider, setLogisticsProvider] = useState('');
  const [logisticsChannel, setLogisticsChannel] = useState('');

  // GRN filters
  const [grnStatus, setGrnStatus] = useState('');
  const [grnSupplier, setGrnSupplier] = useState('');
  const [grnSku, setGrnSku] = useState('');
  const [grnWarehouse, setGrnWarehouse] = useState('');

  // Job Orders filters
  const [joType, setJoType] = useState('');
  const [joStatus, setJoStatus] = useState('');
  const [joDecorator, setJoDecorator] = useState('');
  const [joSku, setJoSku] = useState('');

  // Stocks filters
  const [stockSku, setStockSku] = useState('');
  const [stockWarehouse, setStockWarehouse] = useState('');
  const [stockBrand, setStockBrand] = useState('');

  // Suggestion options (fetched from backend)
  const [channels, setChannels] = useState<string[]>([]);
  const [skus, setSkus] = useState<string[]>([]);
  const [suppliers, setSuppliers] = useState<string[]>([]);
  const [warehouses, setWarehouses] = useState<string[]>([]);
  const [providers, setProviders] = useState<string[]>([]);
  const [decorators, setDecorators] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Preview state
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const previewPageSize = 50;

  // Static options for status fields
  const orderStatusOptions = ['pending', 'confirmed', 'allocated', 'shipped', 'delivered', 'cancelled', 'returned'];
  const shippingStatusOptions = ['booked', 'in_transit', 'out_for_delivery', 'delivered', 'rto', 'cancelled'];
  const paymentStatusOptions = ['pending', 'paid', 'partial', 'refunded'];
  const grnStatusOptions = ['draft', 'received', 'partial', 'completed', 'cancelled'];
  const joTypeOptions = ['outward', 'inward'];
  const joStatusOptions = ['draft', 'confirmed', 'completed', 'cancelled'];

  // Fetch suggestions when report type changes
  const fetchSuggestions = useCallback(async () => {
    if (!activeReport) return;
    setSuggestionsLoading(true);
    try {
      const [channelsRes, skusRes, warehousesRes] = await Promise.all([
        api.get('/api/reports/suggestions/channels').catch(() => ({ data: [] })),
        api.get('/api/reports/suggestions/skus').catch(() => ({ data: [] })),
        api.get('/api/reports/suggestions/warehouses').catch(() => ({ data: [] })),
      ]);
      setChannels(channelsRes.data || []);
      setSkus(skusRes.data || []);
      setWarehouses(warehousesRes.data || []);

      if (activeReport === 'grn') {
        const res = await api.get('/api/reports/suggestions/suppliers').catch(() => ({ data: [] }));
        setSuppliers(res.data || []);
      }
      if (activeReport === 'logistics') {
        const res = await api.get('/api/reports/suggestions/providers').catch(() => ({ data: [] }));
        setProviders(res.data || []);
      }
      if (activeReport === 'job-orders') {
        const res = await api.get('/api/reports/suggestions/decorators').catch(() => ({ data: [] }));
        setDecorators(res.data || []);
      }
      if (activeReport === 'stocks') {
        const res = await api.get('/api/reports/suggestions/brands').catch(() => ({ data: [] }));
        setBrands(res.data || []);
      }
    } catch (e) {
      console.error('Failed to fetch suggestions:', e);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [activeReport]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const loadPreview = async (page = 0) => {
    if (!activeReport) return;
    setPreviewLoading(true);
    try {
      const params: Record<string, any> = { skip: page * previewPageSize, limit: previewPageSize };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      if (activeReport === 'orders') {
        if (orderStatus) params.status = orderStatus;
        if (orderChannel) params.channel = orderChannel;
        if (orderSku) params.sku = orderSku;
        if (shippingStatus) params.shipping_status = shippingStatus;
        if (paymentStatus) params.payment_status = paymentStatus;
      } else if (activeReport === 'logistics') {
        if (logisticsStatus) params.status = logisticsStatus;
        if (logisticsProvider) params.provider = logisticsProvider;
        if (logisticsChannel) params.channel = logisticsChannel;
      } else if (activeReport === 'grn') {
        if (grnStatus) params.status = grnStatus;
        if (grnSupplier) params.supplier = grnSupplier;
        if (grnSku) params.sku = grnSku;
        if (grnWarehouse) params.warehouse = grnWarehouse;
      } else if (activeReport === 'job-orders') {
        if (joType) params.type = joType;
        if (joStatus) params.status = joStatus;
        if (joDecorator) params.decorator = joDecorator;
        if (joSku) params.sku = joSku;
      } else if (activeReport === 'stocks') {
        if (stockSku) params.sku = stockSku;
        if (stockWarehouse) params.warehouse = stockWarehouse;
        if (stockBrand) params.brand = stockBrand;
        // Stocks report doesn't use date filters
        delete params.date_from;
        delete params.date_to;
      }

      const res = await api.get(`/api/reports/${activeReport}/preview`, { params });
      setPreviewData(res.data.rows || []);
      setPreviewTotal(res.data.total || 0);
      setPreviewPage(page);
    } catch (e: any) {
      console.error('Preview failed:', e);
      setPreviewData([]);
      setPreviewTotal(0);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Auto-load preview when report type changes
  useEffect(() => {
    if (activeReport) {
      setPreviewData([]);
      setPreviewTotal(0);
      setPreviewPage(0);
      loadPreview(0);
    }
  }, [activeReport]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetFilters = () => {
    setOrderStatus(''); setOrderChannel(''); setOrderSku(''); setShippingStatus(''); setPaymentStatus('');
    setLogisticsStatus(''); setLogisticsProvider(''); setLogisticsChannel('');
    setGrnStatus(''); setGrnSupplier(''); setGrnSku(''); setGrnWarehouse('');
    setJoType(''); setJoStatus(''); setJoDecorator(''); setJoSku('');
    setStockSku(''); setStockWarehouse(''); setStockBrand('');
    setPreviewData([]); setPreviewTotal(0); setPreviewPage(0);
  };

  const handleExport = async () => {
    if (!activeReport) return;
    setExporting(true);
    setExportError('');
    setExportSuccess('');

    try {
      const params: Record<string, string> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      if (activeReport === 'orders') {
        if (orderStatus) params.status = orderStatus;
        if (orderChannel) params.channel = orderChannel;
        if (orderSku) params.sku = orderSku;
        if (shippingStatus) params.shipping_status = shippingStatus;
        if (paymentStatus) params.payment_status = paymentStatus;
      } else if (activeReport === 'logistics') {
        if (logisticsStatus) params.status = logisticsStatus;
        if (logisticsProvider) params.provider = logisticsProvider;
        if (logisticsChannel) params.channel = logisticsChannel;
      } else if (activeReport === 'grn') {
        if (grnStatus) params.status = grnStatus;
        if (grnSupplier) params.supplier = grnSupplier;
        if (grnSku) params.sku = grnSku;
        if (grnWarehouse) params.warehouse = grnWarehouse;
      } else if (activeReport === 'job-orders') {
        if (joType) params.type = joType;
        if (joStatus) params.status = joStatus;
        if (joDecorator) params.decorator = joDecorator;
        if (joSku) params.sku = joSku;
      } else if (activeReport === 'stocks') {
        if (stockSku) params.sku = stockSku;
        if (stockWarehouse) params.warehouse = stockWarehouse;
        if (stockBrand) params.brand = stockBrand;
        delete params.date_from;
        delete params.date_to;
      }

      const response = await api.get(`/api/reports/${activeReport}/export`, {
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      a.download = `${activeReport}-report-${timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setExportSuccess('Report exported successfully');
    } catch (e: any) {
      console.error('Export failed:', e);
      setExportError(e.response?.data?.detail || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const tiles: Array<{ id: ReportType; label: string; description: string; icon: JSX.Element }> = [
    {
      id: 'orders',
      label: 'Orders Report',
      description: 'Order details with channel, payment & shipping status for reconciliation',
      icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
    },
    {
      id: 'logistics',
      label: 'Logistics Report',
      description: 'Shipment details with AWB, provider, delivery status for vendor billing',
      icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
    },
    {
      id: 'grn',
      label: 'GRN Report',
      description: 'Goods receipts with supplier, quantities & cost for payment verification',
      icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
    },
    {
      id: 'job-orders',
      label: 'Job Orders Report',
      description: 'Decoration job orders with decorator, SKUs & status for vendor reconciliation',
      icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
    },
    {
      id: 'stocks',
      label: 'Stock Reconciliation',
      description: 'Parent & child SKU stock breakdown — track what was received, decorated, and available',
      icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>Reports</h2>
        <p style={{ fontSize: '13px', color: '#64748b' }}>Generate and export filtered reports for audit, reconciliation, and payment verification.</p>
      </div>

      {/* Report Tiles */}
      {!activeReport && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
          {tiles.map(tile => (
            <button
              key={tile.id}
              onClick={() => { setActiveReport(tile.id); resetFilters(); }}
              style={{
                background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px',
                padding: '24px', cursor: 'pointer', textAlign: 'left',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                transition: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = '#0d9488'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.07)'; }}
              onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)'; }}
            >
              <div style={{ color: '#0d9488', marginBottom: '12px' }}>{tile.icon}</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>{tile.label}</div>
              <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>{tile.description}</div>
            </button>
          ))}
        </div>
      )}

      {/* Active Report: Filters + Export */}
      {activeReport && (
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => { setActiveReport(null); setExportError(''); setExportSuccess(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '18px', padding: '4px' }}
                title="Back to reports"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
              </button>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', margin: 0 }}>
                {tiles.find(t => t.id === activeReport)?.label}
              </h3>
            </div>
            <button
              onClick={handleExport}
              disabled={exporting}
              style={{
                background: '#0d9488', color: 'white', border: 'none', borderRadius: '6px',
                padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: exporting ? 'not-allowed' : 'pointer',
                opacity: exporting ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '6px',
                transition: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {exporting ? 'Exporting...' : 'Export XLSX'}
            </button>
          </div>

          {/* Filters */}
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '12px', marginBottom: '16px' }}>
              {/* Common date filters (not for stocks) */}
              {activeReport !== 'stocks' && (
                <>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>Date From</label>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', width: '100%', background: 'white' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>Date To</label>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', width: '100%', background: 'white' }} />
                  </div>
                </>
              )}

              {/* Orders filters */}
              {activeReport === 'orders' && (
                <>
                  <Typeahead label="Order Status" value={orderStatus} onChange={setOrderStatus} options={orderStatusOptions} placeholder="Type status..." />
                  <Typeahead label="Channel" value={orderChannel} onChange={setOrderChannel} options={channels} placeholder="Type channel..." loading={suggestionsLoading} />
                  <Typeahead label="SKU" value={orderSku} onChange={setOrderSku} options={skus} placeholder="Type SKU..." loading={suggestionsLoading} />
                  <Typeahead label="Shipping Status" value={shippingStatus} onChange={setShippingStatus} options={shippingStatusOptions} placeholder="Type shipping status..." />
                  <Typeahead label="Payment Status" value={paymentStatus} onChange={setPaymentStatus} options={paymentStatusOptions} placeholder="Type payment status..." />
                </>
              )}

              {/* Logistics filters */}
              {activeReport === 'logistics' && (
                <>
                  <Typeahead label="Shipping Status" value={logisticsStatus} onChange={setLogisticsStatus} options={shippingStatusOptions} placeholder="Type status..." />
                  <Typeahead label="Provider" value={logisticsProvider} onChange={setLogisticsProvider} options={providers} placeholder="Type provider..." loading={suggestionsLoading} />
                  <Typeahead label="Channel" value={logisticsChannel} onChange={setLogisticsChannel} options={channels} placeholder="Type channel..." loading={suggestionsLoading} />
                </>
              )}

              {/* GRN filters */}
              {activeReport === 'grn' && (
                <>
                  <Typeahead label="GRN Status" value={grnStatus} onChange={setGrnStatus} options={grnStatusOptions} placeholder="Type status..." />
                  <Typeahead label="Supplier" value={grnSupplier} onChange={setGrnSupplier} options={suppliers} placeholder="Type supplier..." loading={suggestionsLoading} />
                  <Typeahead label="SKU" value={grnSku} onChange={setGrnSku} options={skus} placeholder="Type SKU..." loading={suggestionsLoading} />
                  <Typeahead label="Warehouse" value={grnWarehouse} onChange={setGrnWarehouse} options={warehouses} placeholder="Type warehouse..." loading={suggestionsLoading} />
                </>
              )}

              {/* Job Orders filters */}
              {activeReport === 'job-orders' && (
                <>
                  <Typeahead label="Type" value={joType} onChange={setJoType} options={joTypeOptions} placeholder="Type..." />
                  <Typeahead label="Status" value={joStatus} onChange={setJoStatus} options={joStatusOptions} placeholder="Type status..." />
                  <Typeahead label="Decorator / Vendor" value={joDecorator} onChange={setJoDecorator} options={decorators} placeholder="Type decorator..." loading={suggestionsLoading} />
                  <Typeahead label="SKU" value={joSku} onChange={setJoSku} options={skus} placeholder="Type SKU..." loading={suggestionsLoading} />
                </>
              )}

              {/* Stocks filters */}
              {activeReport === 'stocks' && (
                <>
                  <Typeahead label="SKU (Parent or Child)" value={stockSku} onChange={setStockSku} options={skus} placeholder="Type SKU..." loading={suggestionsLoading} />
                  <Typeahead label="Warehouse" value={stockWarehouse} onChange={setStockWarehouse} options={warehouses} placeholder="Type warehouse..." loading={suggestionsLoading} />
                  <Typeahead label="Brand" value={stockBrand} onChange={setStockBrand} options={brands} placeholder="Type brand..." loading={suggestionsLoading} />
                </>
              )}
            </div>

            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span>Type in any filter to search, then click "Load Data" to preview or "Export XLSX" to download.</span>
              <button
                onClick={() => loadPreview(0)}
                disabled={previewLoading}
                style={{
                  background: '#f8f9fa', border: '1px solid #e2e8f0', borderRadius: '6px',
                  padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: previewLoading ? 'not-allowed' : 'pointer',
                  color: '#334155', display: 'flex', alignItems: 'center', gap: '5px',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                {previewLoading ? 'Loading...' : 'Load Data'}
              </button>
            </div>

            {/* Data Preview Table */}
            {previewData.length > 0 && (
              <div style={{ marginTop: '16px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', maxHeight: '420px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafb', position: 'sticky', top: 0, zIndex: 1 }}>
                        {Object.keys(previewData[0]).map(key => (
                          <th key={key} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.5px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                            {key.replace(/_/g, ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }} onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseOut={(e) => e.currentTarget.style.background = 'white'}>
                          {Object.values(row).map((val: any, cidx) => (
                            <td key={cidx} style={{ padding: '7px 10px', whiteSpace: 'nowrap', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', color: '#1e293b' }}>
                              {val !== null && val !== undefined ? String(val) : ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8f9fa', borderTop: '1px solid #e2e8f0', fontSize: '12px', color: '#64748b' }}>
                  <span>Showing {previewPage * previewPageSize + 1} to {Math.min((previewPage + 1) * previewPageSize, previewTotal)} of {previewTotal}</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      disabled={previewPage === 0 || previewLoading}
                      onClick={() => loadPreview(previewPage - 1)}
                      style={{ padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', background: 'white', cursor: previewPage === 0 ? 'not-allowed' : 'pointer', opacity: previewPage === 0 ? 0.5 : 1, fontSize: '12px' }}
                    >Previous</button>
                    <span style={{ padding: '4px 8px', fontSize: '12px', color: '#334155' }}>Page {previewPage + 1} of {Math.ceil(previewTotal / previewPageSize)}</span>
                    <button
                      disabled={(previewPage + 1) * previewPageSize >= previewTotal || previewLoading}
                      onClick={() => loadPreview(previewPage + 1)}
                      style={{ padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', background: 'white', cursor: (previewPage + 1) * previewPageSize >= previewTotal ? 'not-allowed' : 'pointer', opacity: (previewPage + 1) * previewPageSize >= previewTotal ? 0.5 : 1, fontSize: '12px' }}
                    >Next</button>
                  </div>
                </div>
              </div>
            )}

            {previewData.length === 0 && !previewLoading && previewTotal === 0 && activeReport && (
              <div style={{ marginTop: '16px', padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', border: '1px dashed #e2e8f0', borderRadius: '8px' }}>
                Click "Load Data" to preview records matching your filters
              </div>
            )}

            {exportError && (
              <div style={{ marginTop: '12px', padding: '10px 14px', background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '6px', fontSize: '12px', color: '#721c24', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                {exportError}
              </div>
            )}
            {exportSuccess && (
              <div style={{ marginTop: '12px', padding: '10px 14px', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px', fontSize: '12px', color: '#155724', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                {exportSuccess}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsModule;
